import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useMemo, useState } from "react";
import { toId } from "../../lib/subjects";

/* ---------- country ---------- */
const COUNTRY_MAP = {
  UK: "GB",
  "United Kingdom": "GB",
  "U.K.": "GB",
  Ukraine: "UA",
  Italy: "IT",
  USA: "US",
  "United States": "US",
};

const normCountry = (c) => COUNTRY_MAP[c] || (c || "").toUpperCase();

/* ---------- avatar ---------- */
function initialsFrom(name) {
  const s = String(name || "").trim();
  if (!s) return "•";
  const parts = s.split(/\s+/).filter(Boolean);
  const a = parts[0]?.[0] || "";
  const b = parts.length > 1 ? parts[1]?.[0] : parts[0]?.[1] || "";
  return (a + b).toUpperCase();
}

function Avatar({ src, name }) {
  const [broken, setBroken] = useState(false);
  const initials = useMemo(() => initialsFrom(name), [name]);

  const showImg = !!src && !broken;

  return (
    <div className="h-16 w-16 shrink-0 rounded-2xl ring-1 ring-white/10 overflow-hidden">
      {showImg ? (
        <img
          crossOrigin="anonymous"
          src={src}
          alt=""
          className="h-full w-full object-cover"
          loading="lazy"
          onError={() => setBroken(true)}
        />
      ) : (
        <div className="h-full w-full flex items-center justify-center bg-white/10">
          <span className="text-white/80 font-extrabold">{initials}</span>
        </div>
      )}
    </div>
  );
}

/* ---------- main ---------- */
export default function TutorCard({ tutor }) {
  const { t, i18n } = useTranslation();
  // isUk = viewer's language (for UI text direction / name wrapping only)
  const isUk = (i18n.resolvedLanguage || i18n.language || "en").toLowerCase().startsWith("uk");
  // isUAH = specialist's stored currency (never changes based on viewer language)
  const isUAH = (tutor?.currency || "UAH") === "UAH";

  const displayName = tutor?.name || "Tutor";

  /* ---------- location ---------- */
  const [rawCity, ...rest] = ((tutor?.location ?? "") + "").split(",").map((s) => s.trim());
  const rawCountry = rest.join(", ");

  const cityLabel = rawCity ? t(`cities.${rawCity}`, { defaultValue: rawCity }) : "";
  const countryLabel = rawCountry
    ? t(`countries.${normCountry(rawCountry)}`, { defaultValue: rawCountry })
    : "";

  const locationLabel = [cityLabel, countryLabel].filter(Boolean).join(", ");

  /* ---------- subjects ---------- */
  const subjectsRaw = Array.isArray(tutor?.subjects) ? tutor.subjects : [];
  const subjectsLocalized = subjectsRaw.map((s) =>
    t(`subjects.${toId(s)}`, { defaultValue: s })
  );

  /* ---------- RATE FIX (KEY PART) ---------- */
  const rateNum = Number(tutor?.rate);
  const hasRate = Number.isFinite(rateNum);

  // Per-hour label follows viewer's language; currency symbol follows specialist's currency
  const perHour = isUk ? "/год" : "/hr";
  const rateDisplay = hasRate
    ? isUAH
      ? `${rateNum.toLocaleString("uk-UA")} ₴${perHour}`
      : `€${rateNum}${perHour}`
    : null;

  /* ---------- rating ---------- */
  const ratingNum =
    typeof tutor?.rating === "number" && Number.isFinite(tutor.rating)
      ? tutor.rating
      : null;

  const avatarSrc = tutor?.avatar || "";

  return (
    <article className="rounded-2xl bg-white/5 ring-1 ring-white/10 p-5 hover:bg-white/[0.07] hover:ring-white/15 transition">
      <div className="flex items-start gap-4">
        <Avatar src={avatarSrc} name={displayName} />

        <div className="min-w-0 flex-1">
          {/* TOP ROW */}
          <div className="flex items-start justify-between gap-3">
            <h3
              className={
                "font-semibold text-base md:text-lg leading-tight " +
                (isUk ? "line-clamp-2" : "whitespace-nowrap truncate")
              }
              title={displayName}
            >
              {displayName}
            </h3>

            <span className="shrink-0 rounded-xl bg-white/10 px-2.5 py-1 text-xs ring-1 ring-white/10 text-white/85">
              {rateDisplay ?? t("search.priceOnRequest")}
            </span>
          </div>

          {tutor?.fullyVerified ? (
            <span
              className="mt-1 inline-flex items-center rounded-full bg-[#98dea3]/15 px-2 py-0.5 text-[11px] font-semibold text-[#98dea3] ring-1 ring-[#98dea3]/25"
              title={t("credentials.badgeHint")}
            >
              ✓ {t("credentials.badge")}
            </span>
          ) : null}

          {/* LOCATION */}
          {locationLabel ? (
            <p className="mt-1 text-sm text-white/75">{locationLabel}</p>
          ) : (
            <p className="mt-1 text-sm text-white/45">
              {t("search.noLocation", { defaultValue: "Location not set" })}
            </p>
          )}

          {/* ACADEMIC CREDENTIALS */}
          {(tutor?.academicTitle || tutor?.degree || typeof tutor?.experienceYears === "number") && (
            <div className="mt-2 flex flex-wrap items-center gap-x-2 gap-y-1 text-xs">
              {tutor?.academicTitle ? (
                <span className="rounded-md bg-[#98dea3]/12 px-2 py-0.5 text-[#98dea3] ring-1 ring-[#98dea3]/20">
                  {t(`academic.title.${tutor.academicTitle}`, { defaultValue: tutor.academicTitle })}
                  {tutor?.titleVerified ? <span title={t("credentials.confirmedMark")}> ✓</span> : null}
                </span>
              ) : null}
              {tutor?.degree ? (
                <span className="rounded-md bg-white/10 px-2 py-0.5 text-white/75 ring-1 ring-white/10">
                  {t(`academic.degree.${tutor.degree}`, { defaultValue: tutor.degree })}
                  {tutor?.degreeVerified ? <span className="text-[#98dea3]" title={t("credentials.confirmedMark")}> ✓</span> : null}
                </span>
              ) : null}
              {typeof tutor?.experienceYears === "number" ? (
                <span className="text-white/50">{t("academic.expYears", { n: tutor.experienceYears })}</span>
              ) : null}
            </div>
          )}

          {/* SUBJECTS */}
          <div className="mt-3 flex flex-wrap gap-2">
            {subjectsLocalized.length ? (
              subjectsLocalized.slice(0, 4).map((s) => (
                <span
                  key={s}
                  className="rounded-full bg-white/10 ring-1 ring-white/10 px-2.5 py-1 text-xs text-white/80"
                  title={s}
                >
                  {s}
                </span>
              ))
            ) : (
              <span className="text-sm text-white/45">
                {t("search.noSubjects", { defaultValue: "No subjects yet" })}
              </span>
            )}
            {subjectsLocalized.length > 4 && (
              <span className="rounded-full bg-white/5 ring-1 ring-white/10 px-2.5 py-1 text-xs text-white/60">
                +{subjectsLocalized.length - 4}
              </span>
            )}
          </div>

          {/* RATING + BUTTON */}
          <div className="mt-4 flex items-center justify-between gap-3">
            <div className="flex items-center gap-2">
              <RatingStars rating={ratingNum ?? 0} />
              <span className="text-xs text-white/70">
                {ratingNum === null
                  ? t("search.newTutor", { defaultValue: "New" })
                  : ratingNum.toFixed(1)}
              </span>
            </div>

            <Link
              to={`/tutor/${tutor.id}`}
              className="rounded-xl bg-[#7269e3] px-4 py-2 text-sm font-semibold text-white hover:brightness-110"
            >
              {t("search.viewProfile", { defaultValue: "View profile" })}
            </Link>
          </div>
        </div>
      </div>
    </article>
  );
}

/* ---------- stars ---------- */
function Star({ filled = 0 }) {
  if (filled === 1) {
    return (
      <svg viewBox="0 0 24 24" className="h-3.5 w-3.5 text-[#f7cf52]" fill="currentColor">
        <path d="M12 .587l3.668 7.431 8.2 1.193-5.934 5.787 1.402 8.168L12 18.902l-7.336 3.864 1.402-8.168L.132 9.211l8.2-1.193L12 .587z" />
      </svg>
    );
  }
  if (filled === 0.5) {
    return (
      <svg viewBox="0 0 24 24" className="h-3.5 w-3.5 text-[#f7cf52]">
        <defs>
          <linearGradient id="halfStar" x1="0" y1="0" x2="100%" y2="0">
            <stop offset="50%" stopColor="#f7cf52" />
            <stop offset="50%" stopColor="transparent" />
          </linearGradient>
        </defs>
        <path
          d="M12 .587l3.668 7.431 8.2 1.193-5.934 5.787 1.402 8.168L12 18.902l-7.336 3.864 1.402-8.168L.132 9.211l8.2-1.193L12 .587z"
          fill="url(#halfStar)"
          stroke="#f7cf52"
          strokeWidth="1"
        />
      </svg>
    );
  }
  return (
    <svg viewBox="0 0 24 24" className="h-3.5 w-3.5 text-[#f7cf52]" fill="none" stroke="currentColor" strokeWidth="1.5">
      <path d="M12 2.25l2.91 5.897 6.512.947-4.71 4.595 1.111 6.48L12 17.77l-5.823 3.4 1.11-6.48-4.709-4.595 6.512-.947L12 2.25z" />
    </svg>
  );
}

function RatingStars({ rating = 0 }) {
  const safe = Number.isFinite(rating) ? rating : 0;
  const stars = [];
  for (let i = 1; i <= 5; i++) {
    const diff = safe - i + 1;
    const fill = diff >= 1 ? 1 : diff > 0 ? 0.5 : 0;
    stars.push(<Star key={i} filled={fill} />);
  }
  return <div className="flex items-center gap-1">{stars}</div>;
}